import { isNumberBetweenEquals } from "@/shared/utils/number/number";
import {
  ClassNameReaderPanelScrollbar,
  CUSTOM_EVENT_TYPE_READER_ACTION,
  CUSTOM_EVENT_TYPE_READER_PLAY,
  DATA_ATTR_LINK_ID,
  DATA_ATTR_PANEL_INDEX,
  DATA_ATTR_PARA_ID,
  CLASS_NAME_EL_ARROW,
  fontOptions,
  IdReaderDefaultFont,
  READER_OPEN_LINK_BEHAVIOUR,
  READER_PLAYER_BEHAVIOURS,
} from "./constants";
import { dispatchCustomEvent, inRect, makeFnGetFirstVisible } from "../dom";
import { CONTENT_CLASSES } from "../content/content.constants";
import { getAudioChapterByChapterId, getFirstAvailableAudioChapter } from "../player";

export const findTargetId = (para, toc, bookFirstPara) => {
  if (!para || !toc) {
    return undefined;
  }
  let targetId;
  let index = (toc || []).findIndex((item) => item.puborder > para.puborder);
  if (index > 0) {
    index--;
    const chapter = toc[index];
    if (chapter) {
      targetId = chapter.level === 0 ? bookFirstPara || toc[0].id : chapter.id;
    }
  } else if (para.puborder >= (toc[toc.length - 1] || {}).puborder) {
    targetId = toc[toc.length - 1].id;
  } else if (index === -1 && toc.length === 1) {
    targetId = toc[0].id;
  }
  return targetId;
};

export const isParaHeader = (para) => {
  return !!(para && para.element_type && para.element_type[0] === "h");
};

/**
 * @typedef {Object} LinkInPanel
 * @property elem: {},
 * @property id: string,
 * @property activePara: boolean,
 * @property isBible: boolean,
 * @property className: string,
 * @property title: string,
 * @property visible: boolean,
 * @property index: number,
 * @property paraId: string,
 */


/**
 * @returns {{links: LinkInPanel[], panel: Element}}
 */
export const getLinksInPanel = (indexPanel, paraIdPanel) => {
  const panel = document.querySelector(
    `[${DATA_ATTR_PANEL_INDEX}='${indexPanel}'] .${ClassNameReaderPanelScrollbar}`
  ) || document.querySelector( // for lite app
    `[${DATA_ATTR_PANEL_INDEX}='${indexPanel}']`
  );
  let panelRect;

  let links = [];
  if (panel) {
    const egwlinks = panel.getElementsByClassName(CONTENT_CLASSES.EGW_LINK);
    panelRect = panel.getBoundingClientRect();
    let elem,
      elementId,
      elemRect,
      parentPara,
      prevParaId,
      paraId,
      activePara,
      index = 0;

    const computedStyleFirstLink = egwlinks[0] ? window.getComputedStyle(egwlinks[0]) : null;

    let paddingVisibilityVertical;

    if (computedStyleFirstLink) {
      const floatFontSize = parseFloat(computedStyleFirstLink.fontSize);
      const floatLineHeight = parseFloat(computedStyleFirstLink.lineHeight);

      const heightTextLine = floatFontSize * (floatLineHeight / floatFontSize);
      paddingVisibilityVertical = heightTextLine / 2;
    }


    for (let i = 0; i < egwlinks.length; i++) {
      elem = egwlinks[i];
      activePara = false;
      parentPara = elem.closest(".para");

      if (parentPara) {
        paraId = parentPara.getAttribute(DATA_ATTR_PARA_ID);
        if (paraIdPanel && paraId === paraIdPanel) {
          activePara = true;
        }
      }
      elementId = elem.getAttribute(DATA_ATTR_LINK_ID);

      if (prevParaId === paraId) {
        index++;
      } else {
        index = 0;
      }

      prevParaId = paraId;

      if (elem.classList.contains(CONTENT_CLASSES.EGW_LINK_EXTERNAL)) {
        continue;
      }

      elemRect = elem.getBoundingClientRect();

      const isBible = elem.classList.contains(CONTENT_CLASSES.EGW_LINK_BIBLE);

      links.push({
        elem,
        id: elementId,
        activePara,
        isBible,
        className: isBible
          ? CONTENT_CLASSES.EGW_LINK_BIBLE
          : CONTENT_CLASSES.EGW_LINK_BOOK,
        title: elem.innerText,
        visible: inRect(panelRect, elemRect, {
          paddingVertical: paddingVisibilityVertical,
        }),
        index,
        paraId,
      });
    }
  }
  return { panel, panelRect, links };
};

/**
 * @returns {LinkInPanel | null}
 */
export const findNearLink = (
  next, activeLink, activeId, activeIndex, isOpenFirstIfNoLinkActive
) => {
  const { links } = getLinksInPanel(activeIndex, activeId);

  let index = -1;

  // Based on active link
  if (activeLink) {
    index = links.findIndex((item) => {
      return (
        activeLink.paraIdFrom === item.paraId
        && (activeLink.idOriginal || activeLink.id) === item.id
        && activeLink.indexInPara === item.index
      );
    });
  }

  // First visible
  if (index === -1 && isOpenFirstIfNoLinkActive) {
    index = links.findIndex((item) => item.visible);
  }

  // First available link
  if (index === -1 && isOpenFirstIfNoLinkActive && links.length) {
    index = next ? 0 : links.length - 1;
  }

  if (index !== -1) {
    if (!activeLink) {
      return links[index];
    } else if (next && index + 1 < links.length) {
      return links[index + 1];
    } else if (!next && index - 1 >= 0) {
      return links[index - 1];
    }
  }

  return null;
};

export const findLinkBasedOnLinksArea = (idToSearchLinksIn, indexToSearchLinksIn) => {
  const { panelRect, links } = getLinksInPanel(indexToSearchLinksIn, idToSearchLinksIn);

  const arrowElement = document.querySelector(
    `.${CLASS_NAME_EL_ARROW}[${DATA_ATTR_PANEL_INDEX}="${indexToSearchLinksIn}"]`
  );

  if (arrowElement && panelRect) {
    const arrowRect = arrowElement.getBoundingClientRect();

    const areaWidth = panelRect.width;
    const areaHeight = 48;

    const areaX = arrowRect.x;
    const areaY = arrowRect.y - areaHeight / 2;

    const linkSuitable = links.find((link) => {
      const rect = link.elem.getBoundingClientRect();
      const rectX = rect.x;
      const rectY = rect.y;

      return (
        rectX >= areaX &&
        rectY >= areaY &&
        rectX + rect.width <= areaX + areaWidth &&
        rectY + rect.height <= areaY + areaHeight
      );
    });

    if (linkSuitable) {
      return linkSuitable;
    }
  }

  return null;
};

export const findContentLinkById = (idToSearchLinksIn, indexToSearchLinksIn, linkId) => {
  if (linkId) {
    const { links } = getLinksInPanel(indexToSearchLinksIn, idToSearchLinksIn);

    const linkSuitable = links.find((item) => item.id === linkId && item.paraId === idToSearchLinksIn)
      || links.find((item) => item.id === linkId);

    if (linkSuitable) {
      return linkSuitable;
    }
  }

  return null;
};

export const findFirstVisibleLinkInActiveParaOrJustFirstVisible = (idToSearchLinksIn, indexToSearchLinksIn) => {
  const { links } = getLinksInPanel(indexToSearchLinksIn, idToSearchLinksIn);

  return links.find((item) => item.activePara && item.visible)
    || links.find((item) => item.visible)
    || null;
};

export const createReaderActiveLink = (
  linkClassName,
  idOriginal, indexInPara,
  paraIdFrom,
  paraIdsContentPreviewBibleLink,
) => ({
  linkClassName,
  paraIdFrom,
  indexInPara,
  idOriginal,
  paraIdsContentPreviewBibleLink,
});

const allowedContentClass = [
  CONTENT_CLASSES.EGW_LINK,
  CONTENT_CLASSES.EGW_LINK_BIBLE,
  CONTENT_CLASSES.EGW_LINK_BOOK,
];

export const makeActiveLinkSelector = (item) => {
  const { linkClassName, id, paraIdFrom, indexInPara, idOriginal } = item;
  let selectors = [];
  if (paraIdFrom) {
    selectors.push(`.${CONTENT_CLASSES.PARAGRAPH}[data-id='${paraIdFrom}']:not(.tree-link)`);
  }
  if (allowedContentClass.includes(linkClassName)) {
    if (indexInPara !== undefined) {
      selectors.push(`.${linkClassName}`);
    } else {
      selectors.push(`.${linkClassName}[data-link='${idOriginal || id}']`);
    }
    return selectors.join(" ");
  }
  return undefined;
};

export const dispatchCustomEventReaderAction = (action, value) => {
  dispatchCustomEvent(CUSTOM_EVENT_TYPE_READER_ACTION, {action, value});
};

export const getReaderAudioChapter = (toc, chapterId) => {
  if (!toc || !toc[0]) {
    return null;
  }

  const audioChapter = getAudioChapterByChapterId(toc, chapterId);

  if (!audioChapter && chapterId === toc[0].id) {
    return getFirstAvailableAudioChapter(toc);
  }

  return audioChapter;
};

export const getPlayerBehaviorLabel = (settingValue) => {
  if (settingValue === READER_PLAYER_BEHAVIOURS.ASK) {
    return "ask";
  }
  if (settingValue === READER_PLAYER_BEHAVIOURS.HISTORY) {
    return "fromHistory";
  }
  if (settingValue === READER_PLAYER_BEHAVIOURS.CURRENT_PARA) {
    return "currentParagraph";
  }
  if (settingValue === READER_PLAYER_BEHAVIOURS.CONTINUE) {
    return "continuePlaying";
  }
  if (settingValue === READER_PLAYER_BEHAVIOURS.FIRST_AVAILABLE) {
    return "firstAvailable";
  }

  return "";
};

export const dispatchWindowEventPlayAudioInReader = () => {
  window.dispatchEvent(new CustomEvent(CUSTOM_EVENT_TYPE_READER_PLAY));
};

export const getReaderFontConfigById = (fontId) => {
  const font = fontId || IdReaderDefaultFont;

  let fontValue = fontOptions.find((item) => item.id === font);

  if (!fontValue) {
    fontValue = fontOptions[0];
  }

  return fontValue;
};

export const getReaderFirstVisibleId = (parent, checkElementEnd = true, offsetTop = 0) => {
  let firstVisibleDataId;

  makeFnGetFirstVisible(parent, false, false, (elemStart, wrapStart, elemEnd, wrapEnd, elem) => {
    const wrapStartFinal = wrapStart + offsetTop;

    if (elemStart === elemEnd) {
      // h4 bible paras are height 0 - their content is cut off on our side, it's a requirement.
      // But elements are presented in the reader to scroll to the right place.
      return false;
    }

    if (
      elemStart >= wrapStartFinal ||
      (checkElementEnd && elemEnd > wrapStartFinal) ||
      (isNumberBetweenEquals(wrapStartFinal, elemStart, elemEnd) &&
        isNumberBetweenEquals(wrapEnd, elemStart, elemEnd))
    ) {
      firstVisibleDataId = elem.getAttribute(DATA_ATTR_PARA_ID);
      if (firstVisibleDataId) {
        return true;
      }
    }

    return false;
  });

  return firstVisibleDataId;
};

export const makeLinkClassName = (elLink) => {
  if (elLink.classList) {
    if (elLink.classList.contains(CONTENT_CLASSES.EGW_LINK_BIBLE)) {
      return CONTENT_CLASSES.EGW_LINK_BIBLE;
    } else if (elLink.classList.contains(CONTENT_CLASSES.EGW_LINK_BOOK)) {
      return CONTENT_CLASSES.EGW_LINK_BOOK;
    } else if (elLink.classList.contains(CONTENT_CLASSES.EGW_LINK_EXTERNAL)) {
      return CONTENT_CLASSES.EGW_LINK_EXTERNAL;
    }
  }

  return "";
};
